import { Button, Flex, Group, Text, useMantineTheme } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { useEffect, useRef, useState } from "react";
import RichText, { IGetHTML } from "src/components/RichText";
import Select from "src/components/Select";
import TextInput from "src/components/TextInput/TextInput";
import Textarea from "src/components/Textarea/Textarea";
import {
  GetSendgridDynamicFieldManyQuery,
  useCreateSendgridTemplateMutation,
  useGetSendgridDynamicFieldManyQuery,
  TemplateType,
  useGetSendgridTemplateLazyQuery,
  useUpdateSendgridTemplateMutation,
  TriggerListType,
} from "src/generated/graphql";
import { useAppContext } from "src/api/AppContext";
import { object, string } from "yup";

const TRIGGERS_DESCRIPTION = {
  attendanceCancel: "User's booking is cancelled",
  attendanceCreated: "User books an event",
  attendanceNoShow: "User's booking is marked 'No-Show'",
  cancelAccept: "User's agreement cancellation request is approved",
  cancelReject: "User's agreement cancellation request is denied",
  cognitoCreated: "New login is created via club app (Welcome Message)",
  confirmationCode: "New login is created via member app (Confirmation Code)",
  freezeAccept: "User's agreement freeze request is approved",
  freezeReject: "User's agreement freeze request is denied",
  meetingTimeChange: "User's booking time is altered",
  passwordReset: "User requests a password reset",
  paymentMethodChange: "User's saved payment method is altered",
  transactionReject: "User's pending transaction fails",
  transactionSuccess: "User's pending transaction succeeds",
};

interface CreateEditNotificationModalProps {
  locId: string;
  isEdit: boolean;
  itemId?: string;
  onRefetch: () => void;
  onClose: () => void;
}

const sendGridNotificationValidationSchema = object().shape({
  fromEmail: string().when("isEmail", {
    is: (isEmail: boolean) => isEmail,
    then: () => string().email().required("Email field is required"),
  }),
  fromName: string().when("isEmail", {
    is: (isEmail: boolean) => isEmail,
    then: () => string().required("Name field is required"),
  }),
  subjectLine: string().when("isEmail", {
    is: (isEmail: boolean) => isEmail,
    then: () => string().required("Subject field is required"),
  }),
  eventTrigger: string().required(),
  description: string().max(400),
});

const CreateEditNotificationModal = ({
  locId,
  isEdit,
  itemId,
  onRefetch,
  onClose,
}: CreateEditNotificationModalProps) => {
  const theme = useMantineTheme();
  const richTextRef = useRef<IGetHTML>(null);
  const { currentOrganization } = useAppContext();
  const [keywords, setkeywords] = useState<string[]>([]);

  const [isEmail, setIsEmail] = useState(true);
  const [triggerKeywords, setTriggerKeywords] = useState<
    GetSendgridDynamicFieldManyQuery["sendgridDynamicFieldMany"]
  >([]);

  const form = useForm({
    initialValues: {
      fromEmail: "",
      fromName: "",
      subjectLine: "",
      eventTrigger: "",
      description: "",
      isEmail: true,
    },
    validate: yupResolver(sendGridNotificationValidationSchema),
  });

  const triggerEvent = form.getInputProps("eventTrigger").value as string;

  useEffect(() => {
    getKeywordsByTrigger(triggerEvent as TriggerListType);
  }, [triggerEvent]);

  const getKeywordsByTrigger = (trigger: TriggerListType) => {
    const matchingKeywords: string[] = [];
    triggerKeywords?.forEach((keywordObject) => {
      const { keyword, triggers } = keywordObject;
      if (triggers.includes(trigger)) {
        matchingKeywords.push(keyword);
      }
    });
    setkeywords(matchingKeywords);
  };

  const { data: triggers } = useGetSendgridDynamicFieldManyQuery({
    onCompleted: () => {
      if (triggers?.sendgridDynamicFieldMany) {
        setTriggerKeywords(triggers.sendgridDynamicFieldMany);
      }
    },
  });

  const [getSendGridItem] = useGetSendgridTemplateLazyQuery({
    onCompleted: (data) => {
      if (data && data.sendgridTemplateOne) {
        const {
          fromEmail,
          fromName,
          trigger_,
          subjectLine,
          type,
          description,
        } = data.sendgridTemplateOne;
        if (type === TemplateType.Email) {
          setIsEmail(true);
        } else {
          setIsEmail(false);
        }
        form.setFieldValue("fromEmail", fromEmail || "");
        form.setFieldValue("fromName", fromName || "");
        form.setFieldValue("subjectLine", subjectLine || "");
        form.setFieldValue("eventTrigger", trigger_ || "");
        form.setFieldValue("description", description || "");
        form.setFieldValue(
          "isEmail",
          type === TemplateType.Email ? true : false
        );
      }
    },
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (isEdit && itemId) {
      getSendGridItem({
        variables: {
          id: itemId,
        },
      });
    }
  }, [isEdit]);

  const [CreateSendGridTemplate, { loading: createLoading }] =
    useCreateSendgridTemplateMutation({
      onCompleted: () => {
        onRefetch();
        onClose();
      },
    });

  const [UpdateSendGridTemplate, { loading: editLoading }] =
    useUpdateSendgridTemplateMutation({
      onCompleted: () => {
        onRefetch();
        onClose();
      },
    });

  const handleSubmit = (values: any) => {
    const { fromEmail, fromName, subjectLine, eventTrigger, description } =
      values;
    let dataObj = {
      location: { id: locId },
      organization: { id: currentOrganization?.id! },
      name: eventTrigger,
      type: isEmail ? TemplateType.Email : TemplateType.Sms,
      description: richTextRef.current?.getHTML() || "",
      trigger_: eventTrigger,
      isActive: true,
    };

    if (isEmail) {
      Object.assign(dataObj, {
        subjectLine,
        fromEmail,
        fromName,
      });
    }

    if (isEdit) {
      UpdateSendGridTemplate({
        variables: {
          id: itemId!,
          data: {
            ...dataObj,
          },
        },
      });
      return;
    }
    CreateSendGridTemplate({
      variables: {
        data: {
          ...dataObj,
        },
      },
    });
  };

  return (
    <form
      onSubmit={form.onSubmit((values) => {
        handleSubmit(values);
      })}
    >
      <Group position="center" mt="lg" mb="lg" pl={"lg"} pr={"lg"}>
        <Button
          radius="xl"
          compact
          variant="outline"
          onClick={() => {
            setIsEmail(true);
            form.setFieldValue("isEmail", true);
          }}
          style={{
            padding: "0px 50px",
            borderColor: isEmail ? theme.colors.dark[8] : "#DDD",
            borderWidth: isEmail ? 2 : 1,
          }}
          sx={{
            color: theme.colors.primaryTextColor[6],
            "&:hover": {
              backgroundColor: theme.colors.primary[4],
            },
          }}
        >
          Email
        </Button>
        OR
        <Button
          radius="xl"
          compact
          variant="outline"
          onClick={() => {
            setIsEmail(false);
            form.setFieldValue("isEmail", false);
          }}
          style={{
            padding: "0px 50px",
            borderColor: !isEmail ? theme.colors.dark[8] : "#DDD",
            borderWidth: !isEmail ? 2 : 1,
          }}
          sx={{
            color: theme.colors.primaryTextColor[6],
            "&:hover": {
              backgroundColor: theme.colors.primary[4],
            },
          }}
        >
          SMS
        </Button>
      </Group>
      {isEmail ? (
        <>
          <TextInput
            mt={"xs"}
            withAsterisk
            label="From Email"
            {...form.getInputProps("fromEmail")}
          />
          <TextInput
            mt={"xs"}
            withAsterisk
            label="From Name"
            {...form.getInputProps("fromName")}
          />
          <TextInput
            mt={"xs"}
            withAsterisk
            label="Subject Line"
            {...form.getInputProps("subjectLine")}
          />
        </>
      ) : null}
      <Select
        withAsterisk
        data={
          Object.entries(TRIGGERS_DESCRIPTION)?.map((trigger) => ({
            value: trigger[0],
            label: trigger[1],
          })) || []
        }
        label="Event Trigger"
        {...form.getInputProps("eventTrigger")}
        mb={20}
      />

      {triggerEvent ? (
        <>
          <Text size={"sm"} ml={"xs"} fw={500}>
            Keywords Suggestions for drafting content:
          </Text>
          <Text size={"sm"} ml={"xs"} fw={400}>
            Click a keyword to add a dynamic field to your template:
          </Text>
          <RichText
            ref={richTextRef}
            content={form.getInputProps("description").value}
            minHeight={isEmail ? "35vh" : "20vh"}
            key={form.getInputProps("description").value}
            keywords={keywords}
            type={isEmail ? "EMAIL" : "SMS"}
          />
        </>
      ) : // </>
      null}

      <Group position="apart" mt="md" style={{ marginTop: 30 }}>
        <Button
          radius="md"
          variant="outline"
          onClick={onClose}
          style={{
            flex: 1,
            color: theme.colors.secondaryTextColor[6],
          }}
          sx={{
            backgroundColor: theme.colors.secondary[6],
            "&:hover": {
              background: theme.colors.secondary[5],
            },
          }}
        >
          Cancel
        </Button>
        <Button
          radius="md"
          type="submit"
          loading={isEdit ? editLoading : createLoading}
          style={{
            flex: 1,
          }}
          sx={{
            color: theme.colors.primaryTextColor[6],
            "&:hover": {
              backgroundColor: theme.colors.primary[4],
            },
          }}
        >
          Save
        </Button>
      </Group>
    </form>
  );
};

export default CreateEditNotificationModal;
